/**
 * OfferListRow type, the DataTable column definitions, and the bulk-selection
 * surface (toolbar + bulk withdraw-acceptance dialog) — extracted from
 * offer-list-page.tsx to keep that file and its main component within the
 * max-lines / complexity lint budget.
 */
import type { ColumnDef } from '@ooh/ui/components/data-table';
import { Badge } from '@ooh/ui/components/badge';
import { BulkActionToolbar, type BulkAction } from '@ooh/ui/components/bulk-action-toolbar';
import type { OfferListItem } from '@ooh/contracts';
import { getOfferStatusLabelKey, getOfferStatusVariant } from '../lib/offer-status';
import { formatDisplayDate } from '../../../lib/date-format';
import type { OffersT } from '@ooh/screens';
import { WithdrawAcceptanceBulkDialog } from './withdraw-acceptance-bulk-dialog';

export interface OfferListRow {
  brand: string;
  clientName: string;
  offer: OfferListItem;
}

export function buildOfferListColumns(t: OffersT): ColumnDef<OfferListRow>[] {
  return [
    {
      id: 'title',
      label: t('list.columns.title'),
      align: 'left',
      sortable: true,
      sortKey: 'title',
      renderCell: (row: OfferListRow) => {
        const { offer, clientName } = row;
        return (
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate font-semibold leading-snug text-foreground">
              {offer.title}
            </span>
            <span className="truncate text-[12px] text-muted-foreground">
              {clientName || t('list.missingClient')}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      label: t('list.columns.status'),
      align: 'left',
      sortable: true,
      sortKey: 'status',
      width: 140,
      renderCell: (row: OfferListRow) => {
        return (
          <Badge variant={getOfferStatusVariant(row.offer.status)}>
            {t(getOfferStatusLabelKey(row.offer.status))}
          </Badge>
        );
      },
    },
    {
      id: 'campaignNumber',
      label: t('list.columns.campaignNumber'),
      align: 'left',
      sortable: true,
      sortKey: 'campaignNumber',
      width: 160,
      renderCell: (row: OfferListRow) => row.offer.campaignNumber ?? '—',
    },
    {
      id: 'validUntil',
      label: t('list.columns.validUntil'),
      align: 'left',
      sortable: true,
      sortKey: 'validUntil',
      width: 140,
      renderCell: (row: OfferListRow) => formatDisplayDate(row.offer.validUntil),
    },
    {
      id: 'brand',
      label: t('list.columns.brand'),
      align: 'left',
      sortable: true,
      sortKey: 'brand',
      width: 160,
      renderCell: (row: OfferListRow) => row.brand || t('list.missingBrand'),
    },
    {
      id: 'updatedAt',
      label: t('list.columns.updatedAt'),
      align: 'left',
      sortable: true,
      sortKey: 'updatedAt',
      width: 140,
      renderCell: (row: OfferListRow) => formatDisplayDate(row.offer.updatedAt),
    },
  ];
}

/**
 * Bulk-selection toolbar + bulk withdraw-acceptance dialog. Extracted to keep
 * OfferListPage's cyclomatic complexity within the lint budget.
 */
export function OfferListBulkSurface({
  selectedCount,
  visibleCount,
  bulkActions,
  onClearSelection,
  withdrawBulkOpen,
  onCloseWithdrawBulk,
  selectedAcceptedOffers,
  hiddenCount = 0,
  t,
}: {
  selectedCount: number;
  /** Of `selectedCount` (any status), how many a later search hides. */
  visibleCount: number;
  bulkActions: BulkAction[];
  onClearSelection: () => void;
  withdrawBulkOpen: boolean;
  onCloseWithdrawBulk: () => void;
  selectedAcceptedOffers: OfferListItem[];
  /** Of the selected ACCEPTED offers, how many a later search hides. */
  hiddenCount?: number;
  t: OffersT;
}) {
  return (
    <>
      {selectedCount > 0 ? (
        <BulkActionToolbar
          testId="offer-list-bulk-toolbar"
          selectedCount={selectedCount}
          visibleCount={visibleCount}
          visibleLabel={(count) => t('list.bulkWithdrawAcceptance.visibleCountSuffix', { count })}
          aria-label={t('list.bulkWithdrawAcceptance.toolbarAriaLabel')}
          selectedLabel={(count) => t('list.bulkWithdrawAcceptance.selectedCountSuffix', { count })}
          moreLabel={t('list.bulkWithdrawAcceptance.moreActionsLabel')}
          clearLabel={t('list.bulkWithdrawAcceptance.clearSelectionLabel')}
          onClearSelection={onClearSelection}
          actions={bulkActions}
        />
      ) : null}

      {withdrawBulkOpen ? (
        <WithdrawAcceptanceBulkDialog
          open={withdrawBulkOpen}
          onClose={onCloseWithdrawBulk}
          offers={selectedAcceptedOffers}
          hiddenCount={hiddenCount}
        />
      ) : null}
    </>
  );
}
